import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Gallery thumbnail (or swipe/drag) -> Color variant sync.
   *
   * When the customer navigates the gallery themselves (thumbnail click, drag, or scroll — anything
   * with `userInitiated: true`), and the slide they land on is the representative image for one of
   * the variant-picker's own option values (`data-media-id`, set from Shopify's existing
   * `variant.featured_media`, never a hard-coded color/image map), click that option's real radio
   * input — exactly like a customer clicking the swatch themselves. This reuses Horizon's own
   * variant-change flow (native `change` event -> `VariantPicker.variantChanged` -> fetch + morph),
   * so Size and every other currently-selected option are combined the same way Horizon always
   * combines them; nothing here tracks a second/parallel "selected color" of its own.
   *
   * Selecting a color that way triggers a new `ProductSelectEvent`, which replaces this whole
   * `<media-gallery>` element with a freshly rendered one (`#handleProductSelect` above) — a brand
   * new slideshow that initializes its own slide selection programmatically (`userInitiated: false`
   * per `Slideshow#select`), not via this listener, so this does not loop back into itself.
   *
   * @param {SlideshowSelectEvent} event - The slideshow:select event.
   */
  #handleSlideSelect = (event) => {
    if (!event.detail.userInitiated) return;

    const mediaId = event.detail.slide instanceof HTMLElement ? event.detail.slide.dataset.mediaId : undefined;
    if (!mediaId) return; // Media with no associated variant/option value: leave the selection alone.

    const scope = this.closest('.shopify-section, dialog') ?? document;
    const selector = `input[data-media-id="${mediaId}"]`;
    const input =
      scope.querySelector(`variant-picker[data-template-product-match="true"] ${selector}`) ||
      scope.querySelector(`variant-picker ${selector}`);

    if (!(input instanceof HTMLInputElement) || input.checked) return; // Already selected: nothing to do.

    input.click();
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
